/**
 * The one call this window is in (TRUS-668).
 *
 * A tiny external store rather than React context: the call is started from a
 * channel header (MessageThread), joined from a banner or a message card, and
 * rendered by <CallsHost> — three places with no common parent below the app
 * shell. It is per document: a page inside a tab iframe or a pop-out window has
 * its own call, which is what we want (the pop-out takes the call over).
 */

import { useSyncExternalStore } from 'react';
import type { CallJoin, CallMode } from '@/services/callsApi';
import { maybeAskCallNotificationPermission } from './callNotifications';

export interface CallSessionRequest {
  callId: number;
  channelId: number;
  mode: CallMode;
  /** Already joined (start returns a token) — skip POST /join. */
  join?: CallJoin;
  /** Channel / DM name for the panel title. */
  channelName?: string;
  /** The pop-out window that took the call over: fill the window. */
  fullWindow?: boolean;
  /**
   * v2: the opener already knows the call is recording and this user has not
   * consented — ask before POST /join instead of waiting for a 409.
   */
  consentNeeded?: boolean;
  /**
   * v3: an invitee who cannot read the channel (`can_open_channel === false`):
   * the panel opens alone and nothing navigates into the conversation.
   */
  standalone?: boolean;
  /**
   * TRUS-692 meetings hook: a meeting guest on the public /m/{token} page.
   * The panel runs restricted — no screen share, no invites, no notes, no
   * pop-out, no notification prompts — on a guest CallsApi (meetings/guestCallsApi).
   */
  guest?: boolean;
  /** TRUS-692 meetings hook: joined from this meeting (its host gets the in-call lobby). */
  meetingId?: number;
}

/** Asks the messaging workspace (staff) / member inbox to show a call's conversation. */
export const OPEN_CALL_CHANNEL_EVENT = 'weavein:calls:open-channel';

interface CallStoreState {
  session: CallSessionRequest | null;
  /** Element <MessagingWorkspace> provides so the panel docks inside the messaging pane. */
  dock: HTMLElement | null;
  /** Calls this window declined or already handled — never ring for them again. */
  dismissed: Set<number>;
  /**
   * A conversation a call asked to show before the page that shows it was on
   * screen (a ?call= deep link boots before the messaging workspace mounts).
   */
  pendingChannelId: number | null;
  /**
   * v5: a "Calls & notes" search result in another conversation was clicked —
   * that conversation opens and shows its panel on this call.
   */
  notesRequest: { channelId: number; callId: number } | null;
}

const initialState = (): CallStoreState => ({
  session: null,
  dock: null,
  dismissed: new Set(),
  pendingChannelId: null,
  notesRequest: null,
});

let state: CallStoreState = initialState();
const listeners = new Set<() => void>();

function emit(next: Partial<CallStoreState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

export const callStore = {
  get: () => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Open (or replace) the call session. Opening the call already open is a no-op. */
  open(request: CallSessionRequest) {
    if (state.session?.callId === request.callId) return;
    // Joining from a click: the moment to ask for notification permission (once).
    if (!request.fullWindow) maybeAskCallNotificationPermission();
    const dismissed = new Set(state.dismissed);
    dismissed.add(request.callId);
    emit({ session: request, dismissed });
  },
  close() {
    if (state.session) emit({ session: null });
  },
  dismiss(callId: number) {
    if (state.dismissed.has(callId)) return;
    const dismissed = new Set(state.dismissed);
    dismissed.add(callId);
    emit({ dismissed });
  },
  setDock(el: HTMLElement | null) {
    if (state.dock !== el) emit({ dock: el });
  },
  /** Show this call's conversation (never for a standalone invitee — callers check). */
  requestChannel(channelId: number) {
    state = { ...state, pendingChannelId: channelId };
    window.dispatchEvent(new CustomEvent(OPEN_CALL_CHANNEL_EVENT, { detail: { channelId } }));
  },
  /** The conversation still waiting to be shown, once (the page that shows it calls this). */
  takePendingChannel(): number | null {
    const id = state.pendingChannelId;
    if (id != null) state = { ...state, pendingChannelId: null };
    return id;
  },
  /** v5: open a conversation with its "Calls & notes" panel on one call. */
  openCallNotes(channelId: number, callId: number) {
    emit({ notesRequest: { channelId, callId } });
    callStore.requestChannel(channelId);
  },
  /** The call whose notes this conversation should show, once (its header calls this). */
  takeCallNotesRequest(channelId: number): number | null {
    const req = state.notesRequest;
    if (!req || req.channelId !== channelId) return null;
    emit({ notesRequest: null });
    return req.callId;
  },
  /** Tests only. */
  reset() {
    state = initialState();
    listeners.forEach((l) => l());
  },
};

export function useCallStore(): CallStoreState {
  return useSyncExternalStore(callStore.subscribe, callStore.get, callStore.get);
}
